import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getCompatibleUnits, getUnitCategory, COMMON_UNITS } from '../data/foodDatabase';

const { FiX, FiAlertTriangle, FiCheck, FiPlusCircle, FiEdit2, FiPlus } = FiIcons;

const CATEGORIES = ['Protein', 'Carbohydrate', 'Fat', 'Vegetable'];

const CustomFoodForm = ({ onClose, onSave, initialFoodData = null }) => {
  const [foodData, setFoodData] = useState({
    name: '',
    category: 'Protein',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    servingSize: 100,
    servingUnit: 'g',
    servingOptions: [],
    customServingOption: ''
  });
  const [availableOptions, setAvailableOptions] = useState([]);
  const [error, setError] = useState('');
  const [calorieWarning, setCalorieWarning] = useState(null);
  const [showCalorieWarningModal, setShowCalorieWarningModal] = useState(false);
  
  const modalRef = useRef(null);
  const isEditing = !!initialFoodData;

  // Effect to handle unit changes and update available options tags
  useEffect(() => {
    const unit = foodData.servingUnit;
    const compatible = getCompatibleUnits(unit);
    
    // Update the displayed compatible options
    setAvailableOptions(compatible);

    // Automatically reset serving options to defaults when unit category changes drastically
    // unless we are in editing mode and just initialized
    setFoodData(prev => {
      // If the current options list is empty or contains incompatible units, reset it
      const currentOptionsValid = prev.servingOptions.every(opt => {
        const optCat = getUnitCategory(opt);
        const unitCat = getUnitCategory(unit);
        return optCat === unitCat || optCat === 'custom' || unitCat === 'custom';
      });

      if (!currentOptionsValid || prev.servingOptions.length <= 1) {
        // Reset to defaults for this unit type
        // We always include the base unit
        const newOptions = [unit, ...compatible.filter(u => u !== unit)];
        // Filter unique
        const uniqueOptions = [...new Set(newOptions)];
        return { ...prev, servingOptions: uniqueOptions };
      }
      
      // Ensure current unit is always in the list
      if (!prev.servingOptions.includes(unit)) {
        return { ...prev, servingOptions: [unit, ...prev.servingOptions] };
      }

      return prev;
    });
  }, [foodData.servingUnit]);

  // Initial load
  useEffect(() => {
    if (isEditing) {
      setFoodData({
        name: initialFoodData.name,
        category: initialFoodData.category,
        calories: initialFoodData.calories,
        protein: initialFoodData.protein,
        carbs: initialFoodData.carbs,
        fat: initialFoodData.fat,
        servingSize: initialFoodData.servingSize,
        servingUnit: initialFoodData.servingUnit,
        servingOptions: initialFoodData.servingOptions,
        customServingOption: '',
      });
    } else {
      // Set default options for 'g'
      const compatible = getCompatibleUnits('g');
      setFoodData(prev => ({ ...prev, servingOptions: compatible }));
    }
  }, [initialFoodData, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (['calories', 'protein', 'carbs', 'fat', 'servingSize'].includes(name)) {
      if (value === '') {
        finalValue = '';
      } else {
        if (parseFloat(value) < 0) return;
        finalValue = value;
      }
    }
    setFoodData(prev => ({ ...prev, [name]: finalValue }));
  };

  const addCustomServingOption = () => {
    const newOption = foodData.customServingOption.trim();
    if (!newOption) return;
    
    if (foodData.servingOptions.includes(newOption)) {
      setError('This serving option already exists');
      return;
    }
    
    setFoodData(prev => ({
      ...prev,
      servingOptions: [...prev.servingOptions, newOption],
      customServingOption: ''
    }));
    setError('');
  };

  const removeServingOption = (option) => {
    if (option === foodData.servingUnit) return; // Cannot remove base unit
    setFoodData(prev => ({
      ...prev,
      servingOptions: prev.servingOptions.filter(opt => opt !== option)
    }));
  };

  const validateForm = () => {
    if (!foodData.name.trim()) {
      setError('Please enter a food name');
      return false;
    }
    if (foodData.calories === '' || parseFloat(foodData.calories) < 0) {
      setError('Please enter valid calories');
      return false;
    }
    if (!foodData.servingSize || parseFloat(foodData.servingSize) <= 0) {
      setError('Please enter valid serving size');
      return false;
    }

    const p = parseFloat(foodData.protein) || 0;
    const c = parseFloat(foodData.carbs) || 0;
    const f = parseFloat(foodData.fat) || 0;
    const cals = parseFloat(foodData.calories);
    const hasMacros = p > 0 || c > 0 || f > 0;

    if (hasMacros) {
      const calculatedCalories = (p * 4) + (c * 4) + (f * 9);
      const calorieDiscrepancy = Math.abs(calculatedCalories - cals);
      
      if (calorieDiscrepancy > 20) {
        const discrepancyPercentage = (calorieDiscrepancy / (cals || 1)) * 100;
        if (discrepancyPercentage > 20) {
          const warning = {
            enteredCalories: cals,
            calculatedCalories: calculatedCalories,
            discrepancyPercentage: Math.round(discrepancyPercentage)
          };
          setCalorieWarning(warning);
          setShowCalorieWarningModal(true);
          return false;
        }
      }
    }
    return true;
  };

  const getFinalFoodData = () => ({
    name: foodData.name,
    category: foodData.category,
    calories: parseFloat(foodData.calories) || 0,
    protein: parseFloat(foodData.protein) || 0,
    carbs: parseFloat(foodData.carbs) || 0,
    fat: parseFloat(foodData.fat) || 0,
    servingSize: parseFloat(foodData.servingSize),
    servingUnit: foodData.servingUnit,
    servingOptions: foodData.servingOptions,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (validateForm()) {
      onSave(getFinalFoodData());
    } else {
      // Scroll to top to make error visible on mobile
      if (modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleAddAnyway = () => {
    onSave(getFinalFoodData());
    setShowCalorieWarningModal(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={isEditing ? FiEdit2 : FiPlusCircle} className="text-indigo-600" />
            {isEditing ? 'Edit Custom Food' : 'Add Custom Food'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <SafeIcon icon={FiAlertTriangle} className="text-red-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Row 1: Food Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Food Name</label>
            <input
              type="text"
              name="name"
              value={foodData.name}
              onChange={handleChange}
              placeholder="e.g. Pinto Beans"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Row 2: Category, Size, Unit (3 Columns) */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Category (40%) */}
            <div className="md:col-span-5">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
              <div className="relative">
                <select
                  name="category"
                  value={foodData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none bg-white"
                >
                  {CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>

            {/* Serving Size (30%) */}
            <div className="md:col-span-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Size</label>
              <input
                type="number"
                name="servingSize"
                value={foodData.servingSize}
                onChange={handleChange}
                min="0"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Serving Unit (30%) */}
            <div className="md:col-span-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Unit</label>
              <div className="relative">
                <select
                  name="servingUnit"
                  value={foodData.servingUnit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none bg-white"
                >
                  {COMMON_UNITS.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                  {/* Add any custom unit if it's not in common list */}
                  {!COMMON_UNITS.includes(foodData.servingUnit) && (
                    <option value={foodData.servingUnit}>{foodData.servingUnit}</option>
                  )}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Available Unit Options (Box Style) */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Available Unit Options</label>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">
              These are the units that will be compatible with your food in the calculator. We've automatically selected compatible units based on your base unit.
            </p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {foodData.servingOptions.map(option => (
                <div 
                  key={option} 
                  className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all ${
                    option === foodData.servingUnit 
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-200 font-medium' 
                      : 'bg-white text-gray-700 border border-gray-300 shadow-sm'
                  }`}
                >
                  <span>{option}</span>
                  {option !== foodData.servingUnit && (
                    <button 
                      type="button"
                      onClick={() => removeServingOption(option)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="text-xs" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                name="customServingOption"
                value={foodData.customServingOption}
                onChange={handleChange}
                placeholder="Add custom unit"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomServingOption())}
              />
              <button 
                type="button"
                onClick={addCustomServingOption}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                Add
              </button>
            </div>
          </div>

          {/* Row 4: Nutritional Values */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Nutritional Values</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Calories</label>
                <div className="relative">
                  <input
                    type="number"
                    name="calories"
                    value={foodData.calories}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400 text-xs pointer-events-none">kcal</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Protein</label>
                <div className="relative">
                  <input
                    type="number"
                    name="protein"
                    value={foodData.protein}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400 text-xs pointer-events-none">g</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Carbohydrates</label>
                <div className="relative">
                  <input
                    type="number"
                    name="carbs"
                    value={foodData.carbs}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400 text-xs pointer-events-none">g</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">Fat</label>
                <div className="relative">
                  <input
                    type="number"
                    name="fat"
                    value={foodData.fat}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400 text-xs pointer-events-none">g</span>
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-500 italic">
              * Based on serving size of {foodData.servingSize || 0} {foodData.servingUnit}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end pt-6 pb-4 border-t border-gray-200 mt-2">
            <div className="flex gap-3 w-full md:w-auto">
              <button 
                type="button"
                onClick={onClose}
                className="flex-1 md:flex-none px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex-1 md:flex-none px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 font-medium shadow-sm"
              >
                <SafeIcon icon={FiCheck} />
                {isEditing ? 'Save Changes' : 'Add Food'}
              </button>
            </div>
          </div>
        </form>
      </motion.div>

      {showCalorieWarningModal && calorieWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
          onClick={() => setShowCalorieWarningModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-5xl text-orange-500 mx-auto mb-2" />
              <h3 className="text-xl font-bold text-gray-800">Calorie Discrepancy Warning</h3>
            </div>
            
            <p className="text-gray-600 mb-4 text-center">
              The {Math.round(calorieWarning.enteredCalories)} calories entered differ from the {Math.round(calorieWarning.calculatedCalories)} calories calculated from the proteins, carbs, and fats by more than {calorieWarning.discrepancyPercentage}%.
            </p>

            <div className="flex justify-center gap-4 mt-6">
              <button 
                onClick={() => setShowCalorieWarningModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit Food
              </button>
              <button 
                onClick={handleAddAnyway}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Add Anyway
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CustomFoodForm;